import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { transportProviderAPI } from '../../services/api';

const VEHICLE_LABELS = { taxi: '🚕 تاكسي', bus: '🚌 حافلة', rickshaw: '🛺 ركشة' };

const inputCls = 'w-full border-2 border-gray-200 hover:border-blue-400/40 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 rounded-xl px-4 py-2.5 text-sm transition-all outline-none';
const labelCls = 'block text-xs font-bold text-gray-600 mb-1.5';

// Vehicle/driver identity is selected from the provider's own registered
// fleet (see TransportProviderVehiclesPage) — no free text here anymore.
// Only vehicles with driverName+driverPhone already on file are selectable.
const OfferModal = ({ request, vehicles, onClose, onDone }) => {
  const existing = request.myOffer;
  const usableVehicles = vehicles.filter((v) => v.isActive && v.driverName && v.driverPhone);
  const [price, setPrice]         = useState(existing?.price || '');
  const [vehicleId, setVehicleId] = useState(existing?.vehicleId || usableVehicles[0]?.id || '');
  const [etaMinutes, setEtaMinutes] = useState('');
  const [saving, setSaving]       = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!price || parseFloat(price) <= 0) { toast.error('يرجى إدخال سعر صحيح'); return; }
    if (!vehicleId) { toast.error('يرجى اختيار مركبة'); return; }
    setSaving(true);
    try {
      await transportProviderAPI.submitRideOffer(request.id, { price, vehicleId, etaMinutes: etaMinutes || undefined });
      toast.success('تم إرسال العرض');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّر إرسال العرض');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h2 className="font-bold text-gray-800">{existing ? 'تعديل العرض' : 'تقديم عرض'} — {request.requestNumber}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        {usableVehicles.length === 0 ? (
          <div className="p-5 text-sm text-amber-700 bg-amber-50">
            لا توجد مركبة نشطة ببيانات سائق مكتملة. أضف مركبة أو أكمل بيانات السائق من صفحة «أسطولي» أولاً.
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <div>
              <label className={labelCls}>المركبة والسائق *</label>
              <select className={inputCls} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                {usableVehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {VEHICLE_LABELS[v.category] || v.category} — {v.driverName} — {v.plateNumber || 'بلا لوحة'}
                  </option>
                ))}
              </select>
            </div>
            <div><label className={labelCls}>السعر (ج.س) *</label><input type="number" step="0.01" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} required /></div>
            <div><label className={labelCls}>الوصول خلال (دقائق)</label><input type="number" className={inputCls} value={etaMinutes} onChange={(e) => setEtaMinutes(e.target.value)} /></div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white py-2.5 rounded-xl font-semibold transition">
                {saving ? 'جارٍ الإرسال...' : 'إرسال العرض'}
              </button>
              <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const RideJobBoardPage = () => {
  const [requests, setRequests] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [offerFor, setOfferFor] = useState(null);

  const load = () => {
    setLoading(true);
    transportProviderAPI.getOpenRideRequests()
      .then((res) => setRequests(res.data.data || []))
      .catch(() => toast.error('فشل في تحميل الطلبات'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    transportProviderAPI.getVehicles().then((res) => setVehicles(res.data.data || [])).catch(() => {});
  }, []);

  // Best-effort location so the backend can rank the board nearest-first —
  // silently does nothing if the browser/user denies permission, in which
  // case the list just stays in its previous newest-first order.
  useEffect(() => {
    const reportLocation = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => { transportProviderAPI.updateMyLocation(pos.coords.latitude, pos.coords.longitude).catch(() => {}); },
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
      );
    };
    reportLocation();
    const locInterval = setInterval(reportLocation, 5 * 60 * 1000);
    return () => clearInterval(locInterval);
  }, []);

  useEffect(() => {
    load();
    let interval = setInterval(load, 10000); // rides are time-sensitive
    // Pause while the tab is hidden — a background tab polling every 10s
    // across several open pages was adding up to real server load for no
    // benefit, since nothing on screen is visible anyway.
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        load();
        interval = setInterval(load, 10000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return (
    <div dir="rtl" className="p-4 sm:p-6">
      <h1 className="text-xl font-black text-gray-800 mb-1">🚕 طلبات المشاوير المتاحة</h1>
      <p className="text-sm text-gray-500 mb-6">تتحدث تلقائياً كل 10 ثوانٍ — مرتبة حسب الأقرب إليك عند توفر موقعك، ومطابقة لأنواع مركباتك المسجّلة</p>

      {loading ? (
        <div className="text-center text-gray-400 py-16">جاري التحميل...</div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400">
          <div className="text-3xl mb-2">📭</div>
          <p>لا توجد طلبات مشاوير مطابقة الآن</p>
          <p className="text-xs mt-1">تأكد من تفعيل الموقع في متصفحك ومن تسجيل مركبة مطابقة لنوع الطلب في «أسطولي»</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {requests.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono text-gray-400">{r.requestNumber}</span>
                <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-semibold">
                  {VEHICLE_LABELS[r.vehicleCategory] || r.vehicleCategory}
                </span>
              </div>
              <p className="text-sm font-bold text-gray-800 mb-1">{r.fromLabel} ← {r.toLabel}</p>
              <div className="flex gap-3 mb-3 flex-wrap">
                {r.tripDistanceKm != null && <p className="text-xs font-semibold text-gray-600">🛣️ مسافة الرحلة: {r.tripDistanceKm} كم</p>}
                {r.distanceKm != null && <p className="text-xs font-semibold text-blue-600">📍 يبعد عنك {r.distanceKm} كم</p>}
              </div>
              {r.myOffer ? (
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-700">عرضك: {r.myOffer.price} ج.س</span>
                  <button onClick={() => setOfferFor(r)} className="text-xs font-semibold text-blue-700 hover:underline">تعديل</button>
                </div>
              ) : (
                <button onClick={() => setOfferFor(r)} className="w-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold py-2 rounded-xl transition">
                  تقديم عرض
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {offerFor && (
        <OfferModal request={offerFor} vehicles={vehicles} onClose={() => setOfferFor(null)} onDone={() => { setOfferFor(null); load(); }} />
      )}
    </div>
  );
};

export default RideJobBoardPage;
